import React, { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router';
import { Redirect } from 'react-router-dom';
import { useInterviewState } from '../../hooks/useInterviewState';
import { PRIZE_DETAIL_V2_ROUTER, PRIZE_V2_ROUTER } from '../../routes/routes';

import { selectTaxProfileLoading, selectTaxInterviewUrl } from '../../selectors/tax.selector';
import { INTERVIEW_STATUS } from '../../hooks/useInterviewState';
import { Loading } from '../common/Loading';
import { InterviewIframe } from './InterviewIframe';
import { selectAccountState } from '../../selectors/account.selector';
import { AgeGate } from '../access/AgeGate';

interface InterviewContentProps {
    eventName?: string;
    currentUrl: string | undefined;
    loadingUrl: boolean;
}

const InterviewContent: React.FC<InterviewContentProps> = ({
    eventName,
    currentUrl,
    loadingUrl
}) => {
    const { status, shouldRedirect } = useInterviewState();
    const pending = loadingUrl || status === INTERVIEW_STATUS.PENDING;
    const accountState = useSelector(selectAccountState);
    const location = useLocation<{
        prizeId?: string;
        isRetake?: boolean;
    }>();

    const redirects = React.useMemo(
        () => ({
            notLoggedIn: !accountState.isLoggedIn,
            noLocationState: !location.state || !Object.keys(location.state).length,
            noEntitlement: accountState.hasEsportEntitlement === false,
            shouldRedirect
        }),
        [accountState, location.state, shouldRedirect]
    );
    let returnUrl = PRIZE_V2_ROUTER;

    // Back to prize detail page
    if (location.state?.prizeId) {
        returnUrl = `${PRIZE_DETAIL_V2_ROUTER}?prizeId=${location.state?.prizeId}`;
    }
    // refresh competitive with tax tab
    if (location.state?.isRetake) {
        returnUrl = `${PRIZE_V2_ROUTER}?tab=tax`;
    }
    if (redirects.noLocationState || redirects.shouldRedirect) {
        return <Redirect to={returnUrl} />;
    }

    if (pending || !currentUrl)
        return (
            <div style={{ minHeight: '30vh', display: 'flex', alignItems: 'center' }}>
                <Loading />
            </div>
        );

    return <InterviewIframe eventName={eventName} url={currentUrl} />;
};

export const Interview: React.FC = () => {
    const loadingUrl = useSelector(selectTaxProfileLoading);
    const currentUrl = useSelector(selectTaxInterviewUrl);
    const location = useLocation<{
        prizeId?: string;
        eventName?: string;
        isRetake?: boolean;
        dob?: string;
        showAgeGate?: boolean;
    }>();
    const history = useHistory();
    const { eventName, showAgeGate } = location.state || {};
    const [ageVerified, setAgeVerified] = useState(false);

    const handleAgeGateSubmit = useCallback(
        (dob?: string) => {
            history.replace({
                ...location,
                state: {
                    ...location.state,
                    dob,
                    showAgeGate: false
                }
            });
            setAgeVerified(true);
        },
        [history, location]
    );
    if (showAgeGate && !ageVerified) {
        return <AgeGate onSubmit={handleAgeGateSubmit} />;
    }

    return (
        <InterviewContent eventName={eventName} currentUrl={currentUrl} loadingUrl={loadingUrl} />
    );
};
